import React from "react";
import { useLang } from "@/lib/i18n";
import { TrendingUp, Crosshair, LineChart, BadgeCheck } from "lucide-react";

const ICONS = [TrendingUp, Crosshair, LineChart, BadgeCheck];
const IMAGES = [
  "https://images.pexels.com/photos/6495101/pexels-photo-6495101.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.unsplash.com/photo-1607414721186-5309963d7b52?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  "https://images.pexels.com/photos/8636589/pexels-photo-8636589.jpeg?auto=compress&cs=tinysrgb&w=1000",
  "https://images.pexels.com/photos/35898730/pexels-photo-35898730.jpeg?auto=compress&cs=tinysrgb&w=1000",
];

export default function Method() {
  const { t } = useLang();
  const cards = t("method.cards");

  return (
    <section id="method" className="py-20 lg:py-28 relative overflow-hidden" style={{ background: "var(--at-bg)" }}>
      <div className="absolute inset-0 pitch-grid-tactical opacity-40 pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="max-w-2xl mb-12">
          <div className="flex items-center gap-3 mb-4">
            <span className="font-heading text-xs font-extrabold tracking-[0.25em] uppercase gold-gradient-text">03</span>
            <span className="w-8 h-px bg-[var(--trust-gold)]/50" />
            <span className="font-heading text-[11px] font-bold tracking-[0.2em] uppercase text-white/60">{t("method.kicker")}</span>
          </div>
          <h2 className="font-serif-display font-bold tracking-tight text-white text-3xl sm:text-4xl lg:text-5xl leading-tight">{t("method.title")}</h2>
          <p className="mt-4 text-base font-body text-white/75 leading-relaxed">{t("method.subtitle")}</p>
        </div>

        <div className="grid sm:grid-cols-2 gap-6 lg:gap-7">
          {cards.map((c, i) => {
            const Icon = ICONS[i];
            return (
              <div key={i} data-testid={`method-card-${i}`} className="group relative rounded-3xl overflow-hidden at-card-solid transition-[transform,border-color] duration-300 hover:-translate-y-1.5 hover:border-[var(--border-gold)] animate-fade-up" style={{ animationDelay: `${i * 0.08}s` }}>
                <div className="relative h-44 sm:h-48 overflow-hidden">
                  <img src={IMAGES[i]} alt={c.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(56,4,12,0.2) 20%, rgba(56,4,12,0.95) 100%)" }} />
                  <span className="absolute top-4 left-4 text-[11px] font-bold font-heading uppercase tracking-wider text-[#38040C] px-2.5 py-1 rounded-full" style={{ background: "linear-gradient(135deg,#FFE566,#FFD700)" }}>{c.tag}</span>
                  <span className="absolute top-3 right-5 font-serif-display font-bold text-5xl text-white/15 leading-none select-none">0{i + 1}</span>
                </div>
                <div className="relative p-7 sm:p-8">
                  <div className="absolute -top-7 left-7 w-14 h-14 rounded-2xl flex items-center justify-center bg-[#38040C] text-[var(--trust-gold)] shadow-lg ring-1 ring-[var(--border-gold)]"><Icon size={24} /></div>
                  <h3 className="mt-6 font-heading font-bold text-xl tracking-tight text-white">{c.title}</h3>
                  <p className="mt-3 text-sm sm:text-base font-body text-white/75 leading-relaxed">{c.text}</p>
                  <span className="mt-5 block h-0.5 w-10 rounded-full bg-[var(--trust-gold)] transition-[width] duration-500 group-hover:w-24" />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
